import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';

// ─── Step indicators ────────────────────────────────────────────────────────
const STEPS = [
  { id: 1, label: 'Welcome',        icon: 'Sparkles'    },
  { id: 2, label: 'Create Account', icon: 'UserPlus'    },
  { id: 3, label: 'Verify Access',  icon: 'ShieldCheck' },
  { id: 4, label: 'First Plan',     icon: 'Tag'         },
  { id: 5, label: 'All Set!',       icon: 'CheckCircle2'},
];

// ─── Tiny helpers ────────────────────────────────────────────────────────────
const Field = ({ label, children, hint = '' }) => (
  <div className="space-y-1.5">
    <label className="block text-sm font-medium text-foreground">{label}</label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

const TextInput = ({ icon, ...props }) => (
  <div className="relative">
    {icon && (
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <Icon name={icon} size={15} color="var(--color-muted-foreground)" />
      </div>
    )}
    <input
      {...props}
      className={`w-full ${icon ? 'pl-9' : 'pl-3'} pr-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors`}
    />
  </div>
);

const Btn = ({ children, variant = 'primary', loading = false, disabled = false, ...props }) => {
  const base = 'inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed';
  const variants = {
    primary: 'bg-gray-900 hover:bg-gray-800 text-white',
    outline: 'border border-border text-foreground hover:bg-muted',
    ghost:   'text-muted-foreground hover:text-foreground',
  };
  return (
    <button {...props} disabled={disabled || loading} className={`${base} ${variants?.[variant]}`}>
      {loading && <Icon name="Loader2" size={15} color="currentColor" className="animate-spin" />}
      {children}
    </button>
  );
};

const Alert = ({ type = 'error', children }) => {
  const styles = {
    error:   'bg-red-50 border-red-200 text-red-700',
    success: 'bg-green-50 border-green-200 text-green-700',
    info:    'bg-blue-50 border-blue-200 text-blue-700',
  };
  const icons = { error: 'AlertCircle', success: 'CheckCircle2', info: 'Info' };
  return (
    <div className={`flex items-start gap-2.5 border rounded-lg px-4 py-3 text-sm ${styles?.[type]}`}>
      <Icon name={icons?.[type]} size={15} className="mt-0.5 shrink-0" color="currentColor" />
      <span>{children}</span>
    </div>
  );
};

// ─── Step 1 – Welcome ────────────────────────────────────────────────────────
const StepWelcome = ({ onNext }) => (
  <div className="text-center space-y-6">
    <div className="flex justify-center">
      <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-gray-800 to-gray-600 flex items-center justify-center shadow-lg">
        <Icon name="Sparkles" size={36} color="white" />
      </div>
    </div>
    <div>
      <h2 className="text-2xl font-bold text-foreground mb-2">Welcome to WebForms Admin</h2>
      <p className="text-muted-foreground text-sm leading-relaxed max-w-sm mx-auto">
        This wizard will guide you through creating your Super Admin account and configuring the platform — no manual database edits or .env file changes needed.
      </p>
    </div>

    <div className="grid grid-cols-1 gap-3 text-left max-w-sm mx-auto">
      {[
        { icon: 'UserPlus',    text: 'Create your admin account in Supabase' },
        { icon: 'ShieldCheck', text: 'Verify and activate admin privileges'   },
        { icon: 'Tag',         text: 'Set up your first pricing plan'         },
      ]?.map(({ icon, text }) => (
        <div key={text} className="flex items-center gap-3 bg-muted/50 rounded-lg px-4 py-3">
          <div className="w-8 h-8 rounded-lg bg-background border border-border flex items-center justify-center shrink-0">
            <Icon name={icon} size={15} color="var(--color-foreground)" />
          </div>
          <span className="text-sm text-foreground">{text}</span>
        </div>
      ))}
    </div>

    <Btn onClick={onNext}>
      Get Started <Icon name="ArrowRight" size={15} color="white" />
    </Btn>
  </div>
);

// ─── Step 2 – Create Account ─────────────────────────────────────────────────
const StepCreateAccount = ({ onNext, onBack }) => {
  const [form, setForm]     = useState({ fullName: '', email: '', password: '', confirm: '' });
  const [show, setShow]     = useState({ password: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [success, setSuccess] = useState('');

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e?.target?.value }));

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    if (form?.password !== form?.confirm) { setError('Passwords do not match.'); return; }
    if (form?.password?.length < 8)       { setError('Password must be at least 8 characters.'); return; }

    setLoading(true);
    try {
      // Try to sign up first
      const { data: signUpData, error: signUpError } = await supabase?.auth?.signUp({
        email: form?.email,
        password: form?.password,
        options: { data: { full_name: form?.fullName } },
      });

      if (signUpError) {
        // If user already exists, try signing in instead
        if (signUpError?.message?.includes('already registered') || signUpError?.message?.includes('already exists')) {
          const { data: signInData, error: signInError } = await supabase?.auth?.signInWithPassword({
            email: form?.email,
            password: form?.password,
          });
          if (signInError) { setError('Account exists but password is incorrect. Please use your existing password.'); return; }
          setSuccess('Existing account found — signed in successfully!');
          setTimeout(() => onNext({ email: form?.email, userId: signInData?.user?.id, fullName: form?.fullName }), 1200);
          return;
        }
        setError(signUpError?.message);
        return;
      }

      setSuccess('Account created! Check your email to confirm if required, then continue.');
      setTimeout(() => onNext({ email: form?.email, userId: signUpData?.user?.id, fullName: form?.fullName }), 1400);
    } catch (err) {
      setError('Unexpected error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-foreground mb-1">Create Your Admin Account</h2>
        <p className="text-sm text-muted-foreground">This will be your Super Admin login. If you already have an account, enter those credentials.</p>
      </div>
      {error   && <Alert type="error">{error}</Alert>}
      {success && <Alert type="success">{success}</Alert>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="Full Name">
          <TextInput icon="User" type="text" placeholder="Jane Smith" value={form?.fullName} onChange={set('fullName')} required />
        </Field>
        <Field label="Email Address">
          <TextInput icon="Mail" type="email" placeholder="admin@yourcompany.com" value={form?.email} onChange={set('email')} required />
        </Field>
        <Field label="Password" hint="Minimum 8 characters">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon name="Lock" size={15} color="var(--color-muted-foreground)" />
            </div>
            <input
              type={show?.password ? 'text' : 'password'}
              placeholder="Create a strong password"
              value={form?.password}
              onChange={set('password')}
              required
              className="w-full pl-9 pr-10 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
            />
            <button type="button" onClick={() => setShow(s => ({ ...s, password: !s?.password }))} className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground">
              <Icon name={show?.password ? 'EyeOff' : 'Eye'} size={15} color="currentColor" />
            </button>
          </div>
        </Field>
        <Field label="Confirm Password">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon name="Lock" size={15} color="var(--color-muted-foreground)" />
            </div>
            <input
              type={show?.confirm ? 'text' : 'password'}
              placeholder="Repeat your password"
              value={form?.confirm}
              onChange={set('confirm')}
              required
              className="w-full pl-9 pr-10 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
            />
            <button type="button" onClick={() => setShow(s => ({ ...s, confirm: !s?.confirm }))} className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground">
              <Icon name={show?.confirm ? 'EyeOff' : 'Eye'} size={15} color="currentColor" />
            </button>
          </div>
        </Field>

        <div className="flex gap-3 pt-1">
          <Btn type="button" variant="outline" onClick={onBack}>
            <Icon name="ArrowLeft" size={15} color="currentColor" /> Back
          </Btn>
          <Btn type="submit" loading={loading} className="flex-1">
            Create Account <Icon name="ArrowRight" size={15} color="white" />
          </Btn>
        </div>
      </form>
    </div>
  );
};

// ─── Step 3 – Verify / Activate Admin ────────────────────────────────────────
const StepVerifyAccess = ({ accountData, onNext, onBack }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  // Activate via the bootstrap_admin_owner() SECURITY DEFINER RPC.
  // This is the only authorised path — email-match bypasses have been removed.
  // The RPC only succeeds when admin_users is completely empty; it is
  // concurrency-safe (advisory lock) and never upgrades an existing row.
  const activateAdmin = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) { setError('Please sign in first.'); setLoading(false); return; }

      const fullName = accountData?.fullName || user?.user_metadata?.full_name || 'Admin';
      const { data: rpcData, error: rpcError } = await supabase?.rpc(
        'bootstrap_admin_owner',
        { p_full_name: fullName }
      );

      if (rpcError) {
        // Show the actual error — do NOT advance the wizard on failure
        setError(rpcError?.message || 'Admin activation failed. Contact an existing owner to be added as an admin.');
        return;
      }

      console.log('bootstrap_admin_owner result:', rpcData);
      setSuccess('Admin privileges activated successfully!');
      setTimeout(() => onNext(), 1200);
    } catch (err) {
      setError(err?.message || 'Unexpected error during admin activation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-foreground mb-1">Activate Admin Access</h2>
        <p className="text-sm text-muted-foreground">
          Create the first Super Admin record for this platform. This can only be done once — when no admin accounts exist yet.
        </p>
      </div>
      {error   && <Alert type="error">{error}</Alert>}
      {success && <Alert type="success">{success}</Alert>}
      {/* How access works */}
      <div className="bg-muted/40 border border-border rounded-xl p-4 space-y-3">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">How Super Admin Access Works</p>
        <div className="space-y-2">
          {[
            { icon: 'Database',    text: 'Your account is registered as the platform Owner in the database' },
            { icon: 'ShieldCheck', text: 'Future logins at /super-admin-login will grant full access' },
            { icon: 'Users',       text: 'You can then invite additional admins from the Super Admin dashboard' },
          ]?.map(({ icon, text }) => (
            <div key={text} className="flex items-center gap-2.5 text-sm text-foreground">
              <Icon name={icon} size={14} color="var(--color-muted-foreground)" />
              {text}
            </div>
          ))}
        </div>
      </div>
      <div className="flex gap-3">
        <Btn type="button" variant="outline" onClick={onBack}>
          <Icon name="ArrowLeft" size={15} color="currentColor" /> Back
        </Btn>
        <Btn loading={loading} onClick={activateAdmin} className="flex-1">
          <Icon name="ShieldCheck" size={15} color="white" /> Activate Admin Access
        </Btn>
      </div>
    </div>
  );
};

// ─── Step 4 – First Pricing Plan ─────────────────────────────────────────────
const StepFirstPlan = ({ onNext, onBack }) => {
  const [plan, setPlan] = useState({
    name: 'Starter', price: '9', billingPeriod: 'monthly',
    maxForms: '5', maxSubmissions: '500', description: 'Perfect for individuals and small projects',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [skipped, setSkipped] = useState(false);

  const set = (k) => (e) => setPlan(p => ({ ...p, [k]: e?.target?.value }));

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      const { error: insertError } = await supabase?.from('pricing_tiers')?.insert([{
          name: plan?.name,
          price: parseFloat(plan?.price) || 0,
          billing_period: plan?.billingPeriod,
          max_forms: parseInt(plan?.maxForms) || 5,
          max_submissions: parseInt(plan?.maxSubmissions) || 500,
          description: plan?.description,
          is_active: true,
          created_at: new Date()?.toISOString(),
        }]);

      if (insertError) {
        // Table may not exist — skip gracefully
        console.warn('pricing_tiers insert failed:', insertError?.message);
        setSuccess('Plan saved locally. You can manage plans from the Pricing Plans page.');
      } else {
        setSuccess('Pricing plan created successfully!');
      }
      setTimeout(() => onNext(), 1200);
    } catch (err) {
      setError('Could not save plan. You can add plans later from the Pricing Plans page.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-foreground mb-1">Create Your First Pricing Plan</h2>
        <p className="text-sm text-muted-foreground">Set up a plan to offer to your users. You can add more plans later from the Pricing Plans dashboard.</p>
      </div>
      {error   && <Alert type="error">{error}</Alert>}
      {success && <Alert type="success">{success}</Alert>}
      <div className="grid grid-cols-2 gap-4">
        <Field label="Plan Name">
          <TextInput icon="Tag" type="text" placeholder="e.g. Starter" value={plan?.name} onChange={set('name')} />
        </Field>
        <Field label="Price (USD)">
          <TextInput icon="DollarSign" type="number" min="0" step="0.01" placeholder="9.00" value={plan?.price} onChange={set('price')} />
        </Field>
        <Field label="Billing Period">
          <select value={plan?.billingPeriod} onChange={set('billingPeriod')} className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors">
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
            <option value="lifetime">Lifetime</option>
          </select>
        </Field>
        <Field label="Max Forms">
          <TextInput icon="FileText" type="number" min="1" placeholder="5" value={plan?.maxForms} onChange={set('maxForms')} />
        </Field>
        <div className="col-span-2">
          <Field label="Max Submissions / Month">
            <TextInput icon="Inbox" type="number" min="1" placeholder="500" value={plan?.maxSubmissions} onChange={set('maxSubmissions')} />
          </Field>
        </div>
        <div className="col-span-2">
          <Field label="Description">
            <TextInput icon="AlignLeft" type="text" placeholder="Short description for users" value={plan?.description} onChange={set('description')} />
          </Field>
        </div>
      </div>
      <div className="flex gap-3">
        <Btn type="button" variant="outline" onClick={onBack}>
          <Icon name="ArrowLeft" size={15} color="currentColor" /> Back
        </Btn>
        <Btn loading={loading} onClick={handleSave} className="flex-1">
          <Icon name="Plus" size={15} color="white" /> Save Plan & Continue
        </Btn>
      </div>
      <button onClick={() => onNext()} className="w-full text-xs text-muted-foreground hover:text-foreground text-center transition-colors">
        Skip — I'll add plans later
      </button>
    </div>
  );
};

// ─── Step 5 – All Set ────────────────────────────────────────────────────────
const StepAllSet = ({ accountData, navigate }) => (
  <div className="text-center space-y-6">
    <div className="flex justify-center">
      <div className="w-20 h-20 rounded-full bg-green-100 border-4 border-green-200 flex items-center justify-center">
        <Icon name="CheckCircle2" size={40} color="#16a34a" />
      </div>
    </div>
    <div>
      <h2 className="text-2xl font-bold text-foreground mb-2">You're All Set!</h2>
      <p className="text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
        Your Super Admin account is configured. Head to the dashboard to start managing accounts, pricing, and revenue.
      </p>
    </div>

    <div className="bg-muted/40 border border-border rounded-xl p-4 text-left space-y-2 max-w-sm mx-auto">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Your Admin Details</p>
      <div className="flex items-center gap-2 text-sm">
        <Icon name="Mail" size={14} color="var(--color-muted-foreground)" />
        <span className="text-foreground">{accountData?.email || 'Not set'}</span>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <Icon name="ShieldCheck" size={14} color="var(--color-muted-foreground)" />
        <span className="text-foreground">Role: Owner</span>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <Icon name="Link" size={14} color="var(--color-muted-foreground)" />
        <span className="text-foreground">Login at: /super-admin-login</span>
      </div>
    </div>

    <div className="flex flex-col gap-3 max-w-sm mx-auto">
      <Btn onClick={() => navigate('/super-admin-dashboard')}>
        <Icon name="LayoutDashboard" size={15} color="white" /> Go to Super Admin Dashboard
      </Btn>
      <Btn variant="outline" onClick={() => navigate('/super-admin-login')}>
        <Icon name="LogIn" size={15} color="currentColor" /> Go to Admin Login Page
      </Btn>
    </div>
  </div>
);

// ─── Main Onboarding Page ────────────────────────────────────────────────────
const AdminOnboarding = () => {
  const navigate = useNavigate();
  const [step, setStep]             = useState(1);
  const [accountData, setAccountData] = useState({});

  // Check if already logged in as admin
  useEffect(() => {
    const checkSession = async () => {
      const { data: { user } } = await supabase?.auth?.getUser();
      const ownerEmail = import.meta.env?.VITE_SUPER_ADMIN_EMAIL;
      if (user && ownerEmail && user?.email?.toLowerCase() === ownerEmail?.toLowerCase()) {
        // Already configured — offer to skip
      }
    };
    checkSession();
  }, []);

  const next = (data = {}) => {
    setAccountData(prev => ({ ...prev, ...data }));
    setStep(s => Math.min(s + 1, STEPS?.length));
  };
  const back = () => setStep(s => Math.max(s - 1, 1));

  return (
    <>
      <Helmet>
        <title>Admin Setup — WebForms</title>
      </Helmet>
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        {/* Background blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        </div>

        <div className="relative w-full max-w-lg">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <Image
              src="/assets/images/web-forms-logo-1769801123836.png"
              alt="WebForms.biz logo"
              className="h-8 w-auto opacity-80"
            />
          </div>

          {/* Step progress bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              {STEPS?.map((s, i) => {
                const done    = step > s?.id;
                const active  = step === s?.id;
                return (
                  <React.Fragment key={s?.id}>
                    <div className="flex flex-col items-center gap-1">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                        done   ? 'bg-green-500 border-green-500' : active ?'bg-gray-900 border-gray-900': 'bg-background border-border'
                      }`}>
                        {done
                          ? <Icon name="Check" size={14} color="white" />
                          : <Icon name={s?.icon} size={14} color={active ? 'white' : 'var(--color-muted-foreground)'} />
                        }
                      </div>
                      <span className={`text-xs hidden sm:block ${active ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                        {s?.label}
                      </span>
                    </div>
                    {i < STEPS?.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-1 transition-all ${step > s?.id ? 'bg-green-400' : 'bg-border'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            <p className="text-center text-xs text-muted-foreground">Step {step} of {STEPS?.length}</p>
          </div>

          {/* Card */}
          <div className="bg-card border border-border rounded-2xl shadow-xl p-8">
            {step === 1 && <StepWelcome onNext={next} />}
            {step === 2 && <StepCreateAccount onNext={next} onBack={back} />}
            {step === 3 && <StepVerifyAccess accountData={accountData} onNext={next} onBack={back} />}
            {step === 4 && <StepFirstPlan onNext={next} onBack={back} />}
            {step === 5 && <StepAllSet accountData={accountData} navigate={navigate} />}
          </div>

          {/* Already have an account */}
          {step < 5 && (
            <div className="mt-5 text-center">
              <button
                onClick={() => navigate('/super-admin-login')}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5"
              >
                <Icon name="LogIn" size={13} color="currentColor" />
                Already have an admin account? Sign in
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminOnboarding;
